import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { UserAvatar, EfsaneBadge, VerifiedBadge } from "./UserAvatar";
import { mediaUrl, api } from "../lib/api";
import { Search, MessageCircle, Plus, LogOut, Shield, User as UserIcon, Radio } from "lucide-react";
import { NotificationBell } from "./NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export function Navbar() {
  const { user, logout, loading } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [mobileSearch, setMobileSearch] = useState(false);
  const boxRef = useRef();

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/users/search/${encodeURIComponent(q.trim())}`);
        setResults(data);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (!mobileSearch && boxRef.current && !boxRef.current.contains(e.target)) setResults([]); };
    document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [mobileSearch]);

  const goUser = (username) => { navigate(`/u/${username}`); setQ(""); setResults([]); setMobileSearch(false); };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-black/80 border-b border-white/5">
      <div className="mx-auto max-w-[1600px] flex items-center gap-4 sm:gap-6 px-4 sm:px-8 h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <img src={mediaUrl(settings.logo_url) || "/logo.png"} alt={settings.site_name} className="h-8 sm:h-9 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-300">
          <Link to="/" data-testid="nav-home" className="hover:text-white transition-colors">Ana Sayfa</Link>
          <Link to="/create" data-testid="nav-create-room" className="hover:text-white transition-colors">Oda Oluştur</Link>
        </nav>

        <div ref={boxRef} className="relative ml-auto hidden sm:block w-56 lg:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            data-testid="nav-user-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Kullanıcı ara..."
            className="w-full bg-[#2B2B2B] border border-white/10 text-white placeholder-gray-500 rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-[#E50914] transition-all"
          />
          {results.length > 0 && (
            <div className="absolute mt-2 w-full bg-[#1a1a1a] border border-white/10 rounded-lg overflow-hidden shadow-2xl">
              {results.map((u) => (
                <button
                  key={u.id}
                  data-testid={`search-result-${u.username}`}
                  onClick={() => goUser(u.username)}
                  className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-white/5 text-left"
                >
                  <UserAvatar user={u} className="h-8 w-8" />
                  <div>
                    <div className="text-sm text-white">@{u.username}</div>
                    <div className="text-xs text-gray-500">{u.name}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Mobile search toggle */}
        <button data-testid="nav-mobile-search-toggle" onClick={() => setMobileSearch((v) => !v)} className="sm:hidden ml-auto text-gray-300 hover:text-white">
          <Search size={22} />
        </button>

        {user ? (
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/create" data-testid="nav-create-btn-mobile" className="md:hidden text-gray-300 hover:text-white"><Plus size={22} /></Link>
            <NotificationBell />
            <Link to="/messages" data-testid="nav-messages" className="text-gray-300 hover:text-white transition-colors"><MessageCircle size={22} /></Link>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-user-menu" className="outline-none">
                <UserAvatar user={user} premium={user.is_premium} className="h-9 w-9 ring-2 ring-transparent hover:ring-[#E50914] transition-all" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#1a1a1a] border-white/10 text-white w-52">
                <div className="px-2 py-1.5 text-sm">
                  <div className="font-semibold flex items-center gap-1.5">{user.name} {user.verified && <VerifiedBadge />} {user.is_premium && <EfsaneBadge />}</div>
                  <div className="text-xs text-gray-500">@{user.username}</div>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(`/u/${user.username}`)} className="cursor-pointer focus:bg-white/10">
                  <UserIcon size={16} className="mr-2" /> Profilim
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-broadcaster" onClick={() => navigate("/yayinci")} className="cursor-pointer focus:bg-white/10">
                  <Radio size={16} className="mr-2" /> Yayıncı {user.is_broadcaster ? "Paneli" : "Ol"}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/adc")} className="cursor-pointer focus:bg-white/10">
                    <Shield size={16} className="mr-2" /> Yönetim Paneli
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="cursor-pointer focus:bg-white/10 text-red-400">
                  <LogOut size={16} className="mr-2" /> Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : loading ? (
          <div className="h-9 w-9 rounded-full bg-white/10 animate-pulse" data-testid="nav-auth-loading" />
        ) : (
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="text-sm text-gray-200 hover:text-white">Giriş Yap</Link>
            <Link to="/register" data-testid="nav-register" className="text-sm bg-[#E50914] hover:bg-[#F40612] text-white font-medium px-4 py-2 rounded-md transition-all active:scale-95">
              Kayıt Ol
            </Link>
          </div>
        )}
      </div>

      {/* Mobile search bar */}
      {mobileSearch && (
        <div className="sm:hidden px-4 pb-3 border-b border-white/5 bg-black/90">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              autoFocus
              data-testid="nav-user-search-mobile"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Kullanıcı ara..."
              className="w-full bg-[#2B2B2B] border border-white/10 text-white placeholder-gray-500 rounded-full pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#E50914]"
            />
          </div>
          {results.length > 0 && (
            <div className="mt-2 bg-[#1a1a1a] border border-white/10 rounded-lg overflow-hidden">
              {results.map((u) => (
                <button key={u.id} data-testid={`search-result-mobile-${u.username}`} onClick={() => goUser(u.username)}
                  className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-white/5 text-left">
                  <UserAvatar user={u} className="h-8 w-8" />
                  <div><div className="text-sm text-white">@{u.username}</div><div className="text-xs text-gray-500">{u.name}</div></div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </header>
  );
}
